/**
 * Copyright (c) 2024 Angel AI and Terrez Wells. All rights reserved.
 * 
 * This software is the confidential and proprietary property of
 * Angel AI and Terrez Wells. You shall not disclose such
 * Confidential Information and shall use it only in accordance
 * with the terms of the license agreement.
 * 
 * @author Terrez Wells <tezwells5@gmail.com>
 * @version 2.3.0
 */

/**
 * Copyright (c) 2024 Angel AI and Terrez Wells. All rights reserved.
 * 
 * This software is the confidential and proprietary property of
 * Angel AI and Terrez Wells. You shall not disclose such
 * Confidential Information and shall use it only in accordance
 * with the terms of the license agreement.
 * 
 * @author Terrez Wells <tezwells5@gmail.com>
 * @version 2.3.0
 */

import { rlClient } from '../lib/rl-client';

interface Experience {
  state: number[];
  action: string;
  reward: number;
  next_state: number[];
  metadata?: Record<string, any>;
  timestamp?: string;
}

interface Feedback {
  action_id: string;
  reward: number;
  context?: Record<string, any>;
}

export const rlService = {
  async addExperience(experience: Experience) {
    try {
      // Calculate experience value based on metadata
      const analysis = experience.metadata?.analysis;
      if (analysis) {
        const qualityScore = (
          (analysis.coherence * 0.2) +
          (analysis.relevance * 0.3) +
          (analysis.depth * 0.3) +
          (analysis.creativity * 0.2)
        );
        experience.reward *= (1 + qualityScore);
      }

      // Add timestamp if not provided
      const timestampedExperience = {
        ...experience,
        timestamp: experience.timestamp || new Date().toISOString(),
        metadata: {
          ...experience.metadata,
          qualityScore: analysis ? (
            (analysis.coherence * 0.2) +
            (analysis.relevance * 0.3) +
            (analysis.depth * 0.3) +
            (analysis.creativity * 0.2)
          ) : null,
          timestamp: experience.timestamp || new Date().toISOString()
        }
      };

      // Save to local storage first
      const bufferKey = 'rlReplayBuffer';
      const buffer = JSON.parse(localStorage.getItem(bufferKey) || '[]');
      buffer.push(timestampedExperience);
      localStorage.setItem(bufferKey, JSON.stringify(buffer));

      // Try to sync with server
      return rlClient.addExperience(timestampedExperience);
    } catch (error) {
      console.error('Error adding experience:', error);
      // Experience is already saved locally, so we can still use it
      return null;
    }
  },

  async submitFeedback(feedback: Feedback) {
    try {
      // Add quality tracking to feedback
      const storedFeedbacks = JSON.parse(localStorage.getItem('rlFeedback') || '[]');
      const qualityTracking = {
        positiveStreak: 0,
        negativeStreak: 0,
        averageScore: 0
      };

      if (storedFeedbacks.length > 0) {
        const recentFeedbacks = storedFeedbacks.slice(-10);
        qualityTracking.averageScore = recentFeedbacks.reduce((acc: number, f: any) => acc + f.reward, 0) / recentFeedbacks.length;
        
        let streak = 0;
        for (let i = recentFeedbacks.length - 1; i >= 0; i--) {
          if (recentFeedbacks[i].reward > 0) streak++; else break;
        }
        qualityTracking.positiveStreak = streak;
        
        streak = 0;
        for (let i = recentFeedbacks.length - 1; i >= 0; i--) {
          if (recentFeedbacks[i].reward < 0) streak++; else break;
        }
        qualityTracking.negativeStreak = streak;
      }
      // Add timestamp if not in context
      const timestampedFeedback = {
        ...feedback,
        context: {
          ...feedback.context,
          qualityTracking,
          timestamp: new Date().toISOString()
        }
      };

      // Save to local storage first
      storedFeedbacks.push(timestampedFeedback);
      localStorage.setItem('rlFeedback', JSON.stringify(storedFeedbacks));

      // Update model scores
      const modelKey = 'rlModel';
      const model = JSON.parse(localStorage.getItem(modelKey) || '{}');
      if (!model[feedback.action_id]) {
        model[feedback.action_id] = 0;
      }
      model[feedback.action_id] += feedback.reward;
      localStorage.setItem(modelKey, JSON.stringify(model));

      // Try to sync with server
      return rlClient.submitFeedback(timestampedFeedback);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      // Feedback is already saved locally, so we can still use it
      return null;
    }
  },

  async getModelStats() {
    try {
      // Get local stats
      const buffer = JSON.parse(localStorage.getItem('rlReplayBuffer') || '[]');
      const feedbacks = JSON.parse(localStorage.getItem('rlFeedback') || '[]');
      const model = JSON.parse(localStorage.getItem('rlModel') || '{}');

      // Try to get server stats
      const serverStats = await rlClient.getModelStats();

      return {
        experiences: buffer.length + (serverStats?.experiences || 0),
        feedback: feedbacks.length + (serverStats?.feedback || 0),
        model
      };
    } catch (error) {
      console.error('Error getting model stats:', error);
      // Return local stats as fallback
      const buffer = JSON.parse(localStorage.getItem('rlReplayBuffer') || '[]');
      const feedbacks = JSON.parse(localStorage.getItem('rlFeedback') || '[]');
      const model = JSON.parse(localStorage.getItem('rlModel') || '{}');
      return {
        experiences: buffer.length,
        feedback: feedbacks.length,
        model
      };
    }
  },

  async syncLocalData() {
    try {
      await rlClient.syncLocalData();
    } catch (error) {
      console.error('Error syncing local data:', error);
    }
  }
};